'use client';

import { useTranslation } from '@/features/i18n';
import { logger } from '@/utils/loggerUtils';
import { DialogPanel } from '@headlessui/react';
import {
  ClipboardDocumentCheckIcon,
  ClipboardIcon,
  CodeBracketIcon,
  ShareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useLocale } from '@vimond/i18n';
import { getEnvVar } from '@vimond/utils/env';
import { useEffect, useState } from 'react';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  XIcon,
} from 'react-share';
import ModalDialog from '../Modal';

export function MediaShareButton({
  buttonText,
  className,
  contentId,
  contentTitle,
  iconClassName,
}: React.PropsWithChildren<{
  contentId: string;
  buttonText?: string;
  className?: string;
  contentTitle?: string;
  iconClassName?: string;
}>) {
  const [showMediaDialog, setShowMediaDialog] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [pageURL, setPageURL] = useState('');
  const { locale } = useLocale();

  const { t } = useTranslation();

  useEffect(() => {
    const embedContentURL = `${window.location.origin}/embedded/assets/${contentId}?language=${locale}`;
    const embedCode = `<iframe width="800" height="450" src="${embedContentURL}" title="${contentTitle ?? getEnvVar('NEXT_PUBLIC_SITE_NAME') ?? 'VIA Inline Playlist'}" style="border:0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;" allowfullscreen></iframe>`;
    setEmbedCode(embedCode);
    setPageURL(window.location.href);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openMediaDialog(e: React.MouseEvent) {
    e.stopPropagation();
    setShowMediaDialog(true);
  }

  function closeMediaDialog() {
    setShowMediaDialog(false);
  }

  const copyToClipboard = async (text: string, type: 'url' | 'embed') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'url') {
        setUrlCopied(true);
        setTimeout(() => setUrlCopied(false), 2000);
      } else {
        setEmbedCopied(true);
        setTimeout(() => setEmbedCopied(false), 2000);
      }
    } catch (err) {
      logger.error('Failed to copy: ', err);
    }
  };

  const socialMediaDialog = (
    <DialogPanel className="relative w-full max-w-md sm:max-w-lg p-6 sm:my-8 rounded-2xl shadow-2xl bg-background-secondary/80 backdrop-blur-2xl drop-shadow-xl shadow-black/55 overflow-hidden text-left transition-all">
      <XMarkIcon
        className="absolute top-4 right-4 h-5 w-5 cursor-pointer text-text-secondary"
        aria-hidden="true"
        onClick={closeMediaDialog}
      />

      <div className="px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 mt-1 flex items-center gap-3">
          <ShareIcon className="h-6 w-6 text-text-primary" />
          <h3 className="text-xl font-semibold text-text-primary">
            {t('share.video.headline')}
          </h3>
        </div>

        {/* Social Icons */}
        <div className="mb-8 flex justify-center gap-4">
          <FacebookShareButton
            htmlTitle="Facebook"
            hashtag={contentTitle}
            url={pageURL}
          >
            <FacebookIcon
              size={46}
              round
              className="hover:contrast-125 hover:brightness-90"
            />
          </FacebookShareButton>
          <LinkedinShareButton
            htmlTitle="Linkedin"
            title={contentTitle}
            url={pageURL}
          >
            <LinkedinIcon
              size={46}
              round
              className="hover:contrast-125 hover:brightness-90"
            />
          </LinkedinShareButton>
          <TwitterShareButton htmlTitle="X" title={contentTitle} url={pageURL}>
            <XIcon
              size={46}
              round
              className="hover:contrast-125 hover:brightness-90"
            />
          </TwitterShareButton>
        </div>

        {/* Share Link */}
        <div className="mb-4 text-center">
          <p className="text-sm font-semibold text-text-primary">
            {t('share.link')}
          </p>
        </div>
        <div className="mb-6 flex items-center justify-between rounded-lg border border-background-border bg-background-secondary/55 px-4 py-3">
          <span className="mr-3 w-full truncate text-sm text-text-secondary">
            {pageURL}
          </span>
          <button
            className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            onClick={() => copyToClipboard(pageURL, 'url')}
          >
            {urlCopied ? (
              <>
                <ClipboardDocumentCheckIcon className="h-4 w-4" />
                {t('button.copied.text')}
              </>
            ) : (
              <>
                <ClipboardIcon className="h-4 w-4" />
                {t('button.copy.text')}
              </>
            )}
          </button>
        </div>

        {/* Embed Code */}
        {getEnvVar('NEXT_PUBLIC_ENABLE_EMBED_CODE_SHARING') !== 'false' &&
          embedCode && (
            <div className="mb-2">
              <div className="mb-3 flex items-center justify-between px-1">
                <p className="flex items-center gap-1 text-sm font-medium text-text-primary">
                  <CodeBracketIcon className="mr-1 h-4 w-4" /> {t('embed-code')}
                </p>
              </div>
              <div className="rounded-lg border border-background-border">
                <div className="flex items-center justify-between border-b border-background-border bg-background-primary/10 px-3 py-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                    HTML
                  </span>
                  <button
                    className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    onClick={() => copyToClipboard(embedCode, 'embed')}
                  >
                    {embedCopied ? (
                      <>
                        <ClipboardDocumentCheckIcon className="h-4 w-4" />
                        {t('button.copied.text')}
                      </>
                    ) : (
                      <>
                        <ClipboardIcon className="h-4 w-4" />
                        {t('button.copy.text')}
                      </>
                    )}
                  </button>
                </div>
                <div
                  id="embed-code"
                  className="w-full bg-background-secondary/55 px-4 py-2 text-xs font-mono text-text-primary outline-none select-text leading-relaxed overflow-scroll md:h-[90px]"
                >
                  {embedCode}
                </div>
              </div>
            </div>
          )}
      </div>
    </DialogPanel>
  );

  return (
    <button className={className} onClick={openMediaDialog}>
      <ShareIcon
        className={iconClassName ?? 'h-8 w-8 text-white'}
        title="Share"
      />
      {buttonText && <span>{buttonText}</span>}
      <ModalDialog
        showModal={showMediaDialog}
        closeModal={closeMediaDialog}
        modalContent={socialMediaDialog}
      />
    </button>
  );
}
